import { useState } from "react";
import api from "@/lib/api";
import { CheckCircle, ArrowRight, Phone, EnvelopeSimple, MapPin, Star } from "@phosphor-icons/react";
import Seo, { GBP_REVIEW_URL } from "@/components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await api.post("/contact", form); setSubmitted(true); }
    catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Submission failed.");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center" data-testid="contact-success">
        <div className="container-x">
          <div className="max-w-2xl mx-auto surface-card p-12 text-center">
            <CheckCircle size={64} weight="duotone" className="text-[#039855] mx-auto mb-6" />
            <div className="overline mb-3">// MESSAGE LOGGED</div>
            <h1 className="font-heading text-4xl tracking-tighter mb-3">Message received.</h1>
            <p className="text-neutral-300">We will respond within 1 business day.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact — 410-645-0722 · Calverton, MD"
        description="Reach Secrecy Executive Protection at 410-645-0722 or our Calverton, MD office. By-appointment consultations for executive protection, security services, and training enrollment."
        path="/contact"
      />
      <section className="border-b border-white/10 pt-24 pb-12">
        <div className="container-x">
          <div className="overline mb-4">// SIGNAL</div>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tighter">Get in touch.</h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid grid-cols-12 gap-12">
          <div className="col-span-12 lg:col-span-7">
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><div className="label-mono mb-2">Name *</div><input className="field" value={form.name} onChange={update("name")} required data-testid="contact-name" /></div>
                <div><div className="label-mono mb-2">Phone</div><input className="field" type="tel" value={form.phone} onChange={update("phone")} data-testid="contact-phone" /></div>
              </div>
              <div><div className="label-mono mb-2">Email *</div><input className="field" type="email" value={form.email} onChange={update("email")} required data-testid="contact-email" /></div>
              <div><div className="label-mono mb-2">Subject</div>
                <select className="field" value={form.subject} onChange={update("subject")} data-testid="contact-subject">
                  <option>General Inquiry</option><option>Training Question</option><option>Group / Corporate Booking</option><option>Press</option>
                </select>
              </div>
              <div><div className="label-mono mb-2">Message *</div><textarea className="field min-h-32" rows={6} value={form.message} onChange={update("message")} required data-testid="contact-message" /></div>
              {error && <div className="font-mono text-sm text-red-400" data-testid="contact-error">{error}</div>}
              <button type="submit" disabled={loading} className="btn-primary" data-testid="contact-submit">{loading ? "Sending..." : "Send Message"} <ArrowRight size={14} /></button>
            </form>
          </div>

          <aside className="col-span-12 lg:col-span-5">
            <div className="surface-card p-8 space-y-6" data-testid="contact-info">
              <div>
                <div className="overline mb-3">Headquarters</div>
                <div className="flex items-start gap-3 text-neutral-300">
                  <MapPin size={18} className="text-[#FFD300] mt-0.5" />
                  <div>
                    4061 Powder Mill Rd, Suite 500-8<br/>
                    Calverton, MD 20705
                    <div className="mt-2">
                      <a
                        href="https://www.google.com/maps/dir/?api=1&destination=4061+Powder+Mill+Rd+Suite+500-8+Calverton+MD+20705"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-directions"
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-[#FFD300] hover:underline"
                      >
                        Get Directions <ArrowRight size={12} weight="bold" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="divider-line" />
              <div>
                <div className="overline mb-3">By Appointment</div>
                <div className="flex items-center gap-3 text-neutral-300"><Phone size={18} className="text-[#FFD300]" /> <a href="tel:+14106450722" className="hover:text-[#FFD300]">410-645-0722</a></div>
              </div>
              <div className="divider-line" />
              <div>
                <div className="overline mb-3">Email</div>
                <div className="flex items-center gap-3 text-neutral-300"><EnvelopeSimple size={18} className="text-[#FFD300]" /> <a href="mailto:info@seprotection.com" className="hover:text-[#FFD300]">info@seprotection.com</a></div>
              </div>
            </div>

            {/* Google Maps embed */}
            <div className="mt-6 border border-white/10" data-testid="contact-map">
              <iframe
                title="Secrecy Executive Training Academy — Calverton, MD"
                src="https://www.google.com/maps?q=4061+Powder+Mill+Rd+Suite+500-8+Calverton+MD+20705&output=embed"
                className="w-full h-64 block"
                style={{ border: 0, filter: "grayscale(1) contrast(1.15) invert(0.92)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Google review CTA */}
            <a
              href={GBP_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-leave-review"
              className="mt-6 flex items-center justify-between gap-4 p-5 border border-white/10 hover:border-[#FFD300] transition-colors bg-black group"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#FFD300]/10 flex-shrink-0 group-hover:bg-[#FFD300]/20 transition-colors">
                  <Star size={20} weight="fill" className="text-[#FFD300]" />
                </div>
                <div>
                  <div className="font-heading text-base tracking-tight leading-tight">Leave us a Google review</div>
                  <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-400 mt-1">Recent graduates & clients</div>
                </div>
              </div>
              <ArrowRight size={16} className="text-neutral-500 group-hover:text-[#FFD300] group-hover:translate-x-1 transition-all flex-shrink-0" />
            </a>
          </aside>
        </div>
      </section>
    </div>
  );
}
